import { AccountDomainStatus } from '@/client/accounts/types/account-domain.type';
import { Actions, Subjects } from '@/client/users';
import { ViewOnlyRestrictionWhen } from '@/components/custom-roles';
import {
  useAccountDomains,
  useAddDomain,
  useSuppressedDomains,
} from '@/hooks/query';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { DomainInput } from '@/ui';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DomainCard } from './DomainCard';

const StyledDomainContainer = styled.div`
  border: 1px solid var(--gray-dark);
  border-radius: var(--semi-medium-border-radius);
  padding: var(--big-padding);
  overflow: hidden;
  margin-top: var(--medium-big-padding);
`;

const StyledDomainList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xsmall-padding);
`;

type DomainVerificationProps = {
  accountId: string;
  canForceVerify?: boolean;
};

export const DomainVerification: React.FC<DomainVerificationProps> = ({
  accountId,
  canForceVerify = false,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { domains, adminUserCounts, isLoading } = useAccountDomains(accountId);
  const { addDomain, isLoading: isAdding } = useAddDomain(accountId);
  const { suppressedDomains } = useSuppressedDomains();

  const [newDomain, setNewDomain] = useState('');
  const [inlineMessage, setInlineMessage] = useState<{
    text: string;
    variant: 'success' | 'error';
  } | null>(null);

  const { isRestrictedByRole } = usePermission();
  const isAddDomainRestricted = isRestrictedByRole(
    Actions.CREATE,
    Subjects.ACCOUNT_DOMAINS,
  );

  const handleAddDomain = async () => {
    const domainValue = newDomain.toLowerCase().trim();

    if (!domainValue) return;

    setInlineMessage(null);

    const isSuppressed = suppressedDomains?.result?.some(
      (d: { domain: string }) => d.domain === domainValue,
    );

    if (isSuppressed) {
      setInlineMessage({
        text: t('account.domains.error.personal'),
        variant: 'error',
      });
      return;
    }

    const existing = domains?.find((d) => d.domain === domainValue);
    if (existing) {
      if (existing.status === AccountDomainStatus.VERIFIED) {
        setInlineMessage({
          text: t('account.domains.error.alreadyVerified'),
          variant: 'success',
        });
      } else {
        setInlineMessage({
          text: t('account.domains.error.alreadyExists'),
          variant: 'error',
        });
      }
      return;
    }

    try {
      await addDomain({ domain: domainValue });
      setNewDomain('');
      setInlineMessage(null);
      toast?.success(t('toast.success'), t('account.domains.added'));
    } catch {
      toast?.error(t('toast.error'), t('account.domains.addFailed'));
    }
  };

  if (isLoading) {
    return <ProgressSpinner style={{ width: '40px', height: '40px' }} />;
  }

  return (
    <StyledDomainContainer>
      <StyledDomainList>
        {domains?.map((domain) => (
          <DomainCard
            key={domain.id}
            domain={domain}
            accountId={accountId}
            canForceVerify={canForceVerify}
            adminUserCount={adminUserCounts?.[domain.id] ?? 0}
          />
        ))}

        <ViewOnlyRestrictionWhen restricted={isAddDomainRestricted} stretch>
          <DomainInput
            value={newDomain}
            onChange={(value) => {
              setNewDomain(value);
              if (inlineMessage) setInlineMessage(null);
            }}
            onAdd={handleAddDomain}
            placeholder={t('account.domains.addPlaceholder')}
            message={inlineMessage}
            disabled={isAdding || isAddDomainRestricted}
          />
        </ViewOnlyRestrictionWhen>
      </StyledDomainList>
    </StyledDomainContainer>
  );
};
